/**
 * Invitation Store
 *
 * Manages invitation state for coordinating challenges together.
 * NOTE: Invitations are DISTINCT from Gifts - they coordinate doing challenges TOGETHER
 */

import { createWithEqualityFn } from 'zustand/traditional'
import { shallow } from 'zustand/shallow'
import type {
  ChallengeInvitation,
  InvitationPermission,
  InvitationRecipientSearchResult,
  InvitationPermissionType,
  CreateInvitationRequest,
} from '../types'
import { invitationApi } from '../api'

interface InvitationState {
  // Invitations
  sentInvitations: ChallengeInvitation[]
  receivedInvitations: ChallengeInvitation[]
  pendingInvitations: ChallengeInvitation[]

  // Permissions
  permissions: InvitationPermission | null

  // Recipient search
  searchResults: InvitationRecipientSearchResult[]
  isSearching: boolean

  // Loading states
  isLoading: boolean
  isSaving: boolean

  // Error state
  error: string | null

  // Actions - Invitations
  fetchSentInvitations: () => Promise<void>
  fetchReceivedInvitations: () => Promise<void>
  fetchPendingInvitations: () => Promise<void>
  createInvitation: (data: CreateInvitationRequest) => Promise<ChallengeInvitation | null>
  acceptInvitation: (invitationId: string) => Promise<boolean>
  declineInvitation: (invitationId: string) => Promise<boolean>
  cancelInvitation: (invitationId: string) => Promise<boolean>

  // Actions - Permissions
  fetchPermissions: () => Promise<void>
  updatePermissions: (data: {
    permissionType: InvitationPermissionType
    allowedUserIds?: string[]
    blockedUserIds?: string[]
  }) => Promise<boolean>

  // Actions - Search
  searchRecipients: (query: string) => Promise<void>
  clearSearchResults: () => void

  clearError: () => void
  reset: () => void
}

const initialState = {
  sentInvitations: [],
  receivedInvitations: [],
  pendingInvitations: [],
  permissions: null,
  searchResults: [],
  isSearching: false,
  isLoading: false,
  isSaving: false,
  error: null,
}

export const useInvitationStore = createWithEqualityFn<InvitationState>()((set, get) => ({
  ...initialState,

  // ============================================================================
  // Invitations
  // ============================================================================

  fetchSentInvitations: async () => {
    set({ isLoading: true, error: null })

    const response = await invitationApi.getSentInvitations()

    if (response.success && response.data) {
      set({ sentInvitations: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load sent invitations',
        isLoading: false,
      })
    }
  },

  fetchReceivedInvitations: async () => {
    set({ isLoading: true, error: null })

    const response = await invitationApi.getReceivedInvitations()

    if (response.success && response.data) {
      set({ receivedInvitations: response.data, isLoading: false })
    } else {
      set({
        error: response.error ?? 'Failed to load received invitations',
        isLoading: false,
      })
    }
  },

  fetchPendingInvitations: async () => {
    const response = await invitationApi.getPendingInvitations()

    if (response.success && response.data) {
      set({ pendingInvitations: response.data })
    }
  },

  createInvitation: async (data) => {
    set({ isSaving: true, error: null })

    const response = await invitationApi.createInvitation(data)

    if (response.success && response.data) {
      set({
        sentInvitations: [response.data, ...get().sentInvitations],
        isSaving: false,
      })
      return response.data
    } else {
      set({
        error: response.error ?? 'Failed to create invitation',
        isSaving: false,
      })
      return null
    }
  },

  acceptInvitation: async (invitationId) => {
    set({ isSaving: true, error: null })

    const response = await invitationApi.acceptInvitation(invitationId)

    if (response.success && response.data) {
      // Update in all lists
      const updateInvitation = (invitations: ChallengeInvitation[]) =>
        invitations.map((inv) => (inv.id === invitationId ? response.data! : inv))

      set({
        receivedInvitations: updateInvitation(get().receivedInvitations),
        pendingInvitations: get().pendingInvitations.filter((inv) => inv.id !== invitationId),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to accept invitation',
        isSaving: false,
      })
      return false
    }
  },

  declineInvitation: async (invitationId) => {
    set({ isSaving: true, error: null })

    const response = await invitationApi.declineInvitation(invitationId)

    if (response.success && response.data) {
      const updateInvitation = (invitations: ChallengeInvitation[]) =>
        invitations.map((inv) => (inv.id === invitationId ? response.data! : inv))

      set({
        receivedInvitations: updateInvitation(get().receivedInvitations),
        pendingInvitations: get().pendingInvitations.filter((inv) => inv.id !== invitationId),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to decline invitation',
        isSaving: false,
      })
      return false
    }
  },

  cancelInvitation: async (invitationId) => {
    set({ isSaving: true, error: null })

    const response = await invitationApi.cancelInvitation(invitationId)

    if (response.success) {
      set({
        sentInvitations: get().sentInvitations.map((inv) =>
          inv.id === invitationId ? { ...inv, status: 'cancelled' as const } : inv
        ),
        isSaving: false,
      })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to cancel invitation',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Permissions
  // ============================================================================

  fetchPermissions: async () => {
    const response = await invitationApi.getInvitationPermissions()

    if (response.success) {
      set({ permissions: response.data })
    }
  },

  updatePermissions: async (data) => {
    set({ isSaving: true, error: null })

    const response = await invitationApi.updateInvitationPermissions(data)

    if (response.success && response.data) {
      set({ permissions: response.data, isSaving: false })
      return true
    } else {
      set({
        error: response.error ?? 'Failed to update permissions',
        isSaving: false,
      })
      return false
    }
  },

  // ============================================================================
  // Search
  // ============================================================================

  searchRecipients: async (query) => {
    if (query.length < 2) {
      set({ searchResults: [] })
      return
    }

    set({ isSearching: true })

    const response = await invitationApi.searchRecipients(query)

    if (response.success && response.data) {
      set({ searchResults: response.data, isSearching: false })
    } else {
      set({ isSearching: false })
    }
  },

  clearSearchResults: () => set({ searchResults: [] }),

  clearError: () => set({ error: null }),

  reset: () => set(initialState),
}))

// Selectors
export const selectSentInvitations = (state: InvitationState) => state.sentInvitations
export const selectReceivedInvitations = (state: InvitationState) => state.receivedInvitations
export const selectPendingInvitations = (state: InvitationState) => state.pendingInvitations
export const selectPendingInvitationCount = (state: InvitationState) => state.pendingInvitations.length
